import { makeStyles } from 'tss-react/mui';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { CartItem } from '../../../utils/cart';
import {
  checkForUnavailableRequiredModifierGroup,
  checkItemInCartItemTree,
  isItem86edToday,
  splitPathToItem,
} from '../../../utils/menu';
import MultipleSelectionModGroup from './MultipleSelectionModGroup';
import SingularSelectionModGroup from './SingularSelectionModGroup';
import Delayed from './Delayed';
import { ParsedMenuItem, ParsedModifierGroup } from '../../../types/menu';
import { getMenuUnificationFeatureFlag } from '../../../utils/session-storage';
import { useSelector } from 'react-redux';
import { selectDialogHighlightedModifier } from '../../../selectors/dialog';
import Colors from '../../../utils/color';
import logger from '../../../utils/logger';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import { selectUnavailableItemsByName } from '../../../selectors/menu';
import useTTSMessage from '../../../hooks/useTTSMessage.hooks';

interface OwnProps {
  modGroup: ParsedModifierGroup;
  cartItem: CartItem;
  selectMenuItemModifier: (
    menuItem: ParsedMenuItem,
    modGroup: ParsedModifierGroup,
    pathToItem: string,
    selected: boolean,
    fromClick?: boolean,
    isDefaultSelection?: boolean
  ) => void;
  pathToItem: string;
  isSelectedToFocus?: boolean; // Selected by Tab key
  isHighlighted?: boolean; // Highlighted in Omnibar search result
}

export interface IModGroupStyleProps {
  isSelectedToFocus?: boolean;
}

const useStyles = makeStyles<IModGroupStyleProps>()(
  ({ spacing }, { isSelectedToFocus }) => {
    const bgColor = isSelectedToFocus ? Colors.alto : Colors.white;
    return {
      modGroupClass: {
        marginLeft: spacing(1.5),
        backgroundColor: bgColor,
      },
    };
  }
);

const ModGroup = (props: OwnProps) => {
  const modGroupRef = useRef<HTMLDivElement>(null);
  const {
    modGroup,
    cartItem,
    selectMenuItemModifier,
    pathToItem,
    isSelectedToFocus,
  } = props;

  const isBrandMenu = getMenuUnificationFeatureFlag();
  const highlightedModifier = useSelector(selectDialogHighlightedModifier);
  const unavailableItemsByName = useSelector(selectUnavailableItemsByName);
  const { sendModGroupTTSMessage } = useTTSMessage();

  const isHighlighted = highlightedModifier === pathToItem;
  let parentModifier = '';
  const nodesInPath = splitPathToItem(pathToItem);
  if (nodesInPath.length > 2) {
    parentModifier = nodesInPath[nodesInPath.length - 2];
  }

  const { classes } = useStyles({
    isSelectedToFocus,
  });

  useEffect(() => {
    if (isSelectedToFocus || isHighlighted) {
      modGroupRef.current?.scrollIntoView();
    }
  }, [isSelectedToFocus, isHighlighted]);

  const allModifierUnavailable = !Object.values(modGroup.menuItems).find(
    (menuItem) => {
      if (
        (menuItem.available || isItem86edToday(menuItem)) &&
        !checkForUnavailableRequiredModifierGroup(menuItem.modifierGroups)
      ) {
        return menuItem;
      }
    }
  );
  const cartModGroup = checkItemInCartItemTree({ cartItem, pathToItem });

  useEffect(() => {
    // handle default modifier only when it doesn't exit in the cartModGroup
    if (!cartModGroup) {
      modGroup.defaultSelectedItemIds?.forEach((itemId: string | number) => {
        if (
          isBrandMenu &&
          isBMModifierAnUnavailableConstraint(
            cartItem,
            unavailableItemsByName,
            parentModifier,
            pathToItem,
            modGroup.name,
            String(itemId)
          )
        ) {
          logger.log(
            `In ModGroup, not selecting default item ${itemId} in group ${modGroup.id} for cartItem ${cartItem.name} as it is present in brand menu constraints`
          );
        } else {
          const matchingItems = Object.values(modGroup.menuItems).filter(
            (item) => item.itemId === String(itemId)
          );

          if (!matchingItems.length) {
            logger.log({
              message: `Warning: Default item ${itemId} not found in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
            });
          } else if (matchingItems.length > 1) {
            logger.log({
              message: `Warning: Multiple items found with itemId ${itemId} in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
            });
          }

          matchingItems.forEach((item) =>
            selectMenuItemModifier(
              item,
              modGroup,
              pathToItem,
              true,
              false,
              true
            )
          );
        }
      });
    }
  }, [cartModGroup, pathToItem]);

  const isSingleChoice =
    modGroup.minimumSelections === modGroup.maximumSelections &&
    modGroup.maximumSelections === 1;

  const handleModChange = useCallback(
    (innerModGroup: ParsedModifierGroup) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const itemId = event.target.value;
        const isChecked = isSingleChoice || event.target.checked;
        if (itemId.length > 0) {
          selectMenuItemModifier(
            innerModGroup.menuItems[itemId],
            innerModGroup,
            pathToItem,
            isChecked,
            true,
            false
          );
        }
      },
    [isSingleChoice, pathToItem, selectMenuItemModifier]
  );

  const modGroupProps = {
    cartItem,
    key: `${modGroup.id}_main_node`,
    modGroup,
    cartModGroup,
    onChange: handleModChange,
    selectMenuItemModifier,
    isSelectedToFocus,
    isHighlighted,
    parentModifier: isBrandMenu ? parentModifier : '',
    sendModGroupTTSMessage,
  };

  return (
    <Delayed>
      <div
        data-testid="mod-group"
        className={classes.modGroupClass}
        ref={modGroupRef}
      >
        {allModifierUnavailable ? null : (
          <>
            {isSingleChoice ? (
              <SingularSelectionModGroup
                {...modGroupProps}
                pathToItem={pathToItem}
              />
            ) : (
              <MultipleSelectionModGroup
                {...modGroupProps}
                pathToItem={pathToItem}
              />
            )}
          </>
        )}
      </div>
    </Delayed>
  );
};

export default memo(ModGroup);
